/**
 * NTF API client — typed thin wrappers over the shared credentialed http core.
 * Every call here maps 1:1 to a real route in apps/api/src/modules/ntf/** so the
 * front-end call-table diffs clean against the route table (no-dead-UI gate).
 *
 * The Export control deliberately hits a 501 route — "wired to fail loudly".
 */
import { http } from '@teivor/shared-utils/api';

import type {
  ColdPreviewResult,
  CreateTemplateInput,
  DeliveryLogQuery,
  DeliveryLogRow,
  MergeVar,
  NotificationItem,
  NotificationQuery,
  Paginated,
  PreviewInput,
  SlaSettings,
  Template,
  TemplateListQuery,
  TemplateVersion,
  UpdateSlaInput,
  UpdateTemplateInput,
} from './types';

export const ntfApi = {
  // ----- Page 1: Templates -------------------------------------------------
  listTemplates: (q: TemplateListQuery) =>
    http.get<Paginated<Template>>('/api/ntf/templates', {
      status: q.status,
      mode: q.mode,
      coldOnly: q.coldOnly,
      page: q.page,
      pageSize: q.pageSize,
    }),
  getTemplate: (id: string) => http.get<Template>(`/api/ntf/templates/${id}`),
  createTemplate: (body: CreateTemplateInput) =>
    http.post<Template>('/api/ntf/templates', body),
  updateTemplate: (id: string, body: UpdateTemplateInput) =>
    http.patch<Template>(`/api/ntf/templates/${id}`, body),
  archiveTemplate: (id: string, version: number) =>
    http.post<Template>(`/api/ntf/templates/${id}/archive`, { version }),
  restoreTemplate: (id: string, version: number) =>
    http.post<Template>(`/api/ntf/templates/${id}/restore`, { version }),
  listVersions: (id: string) =>
    http.get<Paginated<TemplateVersion>>(`/api/ntf/templates/${id}/versions`, {
      page: 1,
      pageSize: 50,
    }),
  preview: (id: string, body: PreviewInput) =>
    http.post<ColdPreviewResult>(`/api/ntf/templates/${id}/preview`, body),

  // ----- Page 1: Merge variables (read-only) ------------------------------
  listMergeVars: (scope?: 'person' | 'company') =>
    http.get<MergeVar[]>('/api/ntf/merge-vars', scope ? { scope } : undefined),

  // ----- Page 2: SLAs ------------------------------------------------------
  getSlaSettings: () => http.get<SlaSettings>('/api/ntf/settings/slas'),
  updateSlaSettings: (body: UpdateSlaInput) =>
    http.patch<SlaSettings>('/api/ntf/settings/slas', body),

  // ----- Page 3: Notifications feed ---------------------------------------
  listNotifications: (q: NotificationQuery) =>
    http.get<Paginated<NotificationItem>>('/api/ntf/notifications', {
      kind: q.kind,
      priority: q.priority,
      page: q.page,
      pageSize: q.pageSize,
    }),
  unreadCount: () =>
    http.get<{ count: number }>('/api/ntf/notifications/unread-count'),
  markRead: (id: string, version?: number) =>
    http.patch<NotificationItem>(`/api/ntf/notifications/${id}/read`, { version }),
  markAllRead: () =>
    http.post<{ affected: number }>('/api/ntf/notifications/read-all', {}),

  // ----- Page 4: Transactional Delivery Log -------------------------------
  listDeliveryLog: (q: DeliveryLogQuery) =>
    http.get<Paginated<DeliveryLogRow>>('/api/ntf/system/delivery-log', {
      status: q.status,
      eventType: q.eventType,
      from: q.from,
      to: q.to,
      page: q.page,
      pageSize: q.pageSize,
    }),
  /** Always 501 — surfaced as an honest "not available" toast + panel. */
  exportDeliveryLog: (q: DeliveryLogQuery) =>
    http.get<never>('/api/ntf/system/delivery-log/export', {
      status: q.status,
      eventType: q.eventType,
      from: q.from,
      to: q.to,
    }),
};
